import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { healthDevicesAPI, patientChartAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Watch,
  Heart,
  Drop,
  Lightning,
  Moon,
  Footprints,
  Thermometer,
  SpinnerGap,
  Scales,
  Pulse,
  TrendUp,
  TrendDown,
  Minus,
  User,
  MagnifyingGlass,
  ChartLine,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const statusColors = {
  normal: 'bg-green-100 text-green-800 border-green-200',
  high: 'bg-red-100 text-red-800 border-red-200',
  low: 'bg-yellow-100 text-yellow-800 border-yellow-200'
};

const dataTypeIcons = {
  glucose: <Drop size={20} className="text-green-600" />,
  blood_pressure_systolic: <Heart size={20} className="text-red-600" />,
  heart_rate: <Heart size={20} className="text-red-500" />,
  steps: <Footprints size={20} className="text-blue-500" />,
  weight: <Scales size={20} className="text-teal-500" />,
  temperature: <Thermometer size={20} className="text-orange-500" />,
  sleep_hours: <Moon size={20} className="text-indigo-500" />,
  hrv: <Pulse size={20} className="text-purple-500" />,
  blood_oxygen: <Pulse size={20} className="text-blue-600" />
};

export default function ProviderHealthDevicesPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSummary, setPatientSummary] = useState(null);
  const [glucoseData, setGlucoseData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const res = await patientChartAPI.getPatientsList();
      setPatients(res.data.patients || []);
    } catch (error) {
      console.error('Error loading patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const loadPatientSummary = async (patientId) => {
    try {
      setLoadingSummary(true);
      const [summaryRes, glucoseRes] = await Promise.all([
        healthDevicesAPI.getSummary(patientId),
        healthDevicesAPI.getGlucoseChart(patientId, 7)
      ]);
      setPatientSummary(summaryRes.data);
      setGlucoseData(glucoseRes.data);
    } catch (error) {
      console.error('Error loading patient summary:', error);
      toast.error('Failed to load patient health data');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    loadPatientSummary(patient.id);
  };

  const filteredPatients = patients.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendUp size={16} className="text-red-500" />;
    if (direction === 'down') return <TrendDown size={16} className="text-green-500" />;
    return <Minus size={16} className="text-slate-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <SpinnerGap size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-teal-100 rounded-lg">
          <Watch size={28} weight="duotone" className="text-teal-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Patient Health Devices
          </h1>
          <p className="text-slate-500 text-sm">View connected device data for your patients</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Patients</CardTitle>
              <div className="relative mt-2">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search patients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="provider-hd-search-input"
                />
              </div>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              <div className="space-y-2">
                {filteredPatients.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No patients found</p>
                ) : (
                  filteredPatients.map(patient => (
                    <div
                      key={patient.id}
                      data-testid={`provider-hd-patient-${patient.id}`}
                      onClick={() => handleSelectPatient(patient)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedPatient?.id === patient.id 
                          ? 'bg-teal-50 border border-teal-200' 
                          : 'hover:bg-slate-50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                          <User size={20} className="text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium">{patient.first_name} {patient.last_name}</p>
                          <p className="text-xs text-slate-500">{patient.email}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Patient Health Summary */}
        <div className="lg:col-span-2">
          {!selectedPatient ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Watch size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">Select a Patient</h3>
                <p className="text-slate-500 mt-1">Choose a patient to view their connected health device data</p>
              </CardContent>
            </Card>
          ) : loadingSummary ? (
            <Card>
              <CardContent className="py-12 text-center">
                <SpinnerGap size={32} className="mx-auto animate-spin text-teal-600" />
                <p className="text-slate-500 mt-4">Loading health data...</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Patient Header */}
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                        <User size={24} className="text-teal-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold">{selectedPatient.first_name} {selectedPatient.last_name}</h2>
                        <p className="text-sm text-slate-500">{selectedPatient.email}</p>
                      </div>
                    </div>
                    <div>
                      <Badge className="bg-teal-100 text-teal-800">
                        {patientSummary?.device_count || 0} Device{patientSummary?.device_count !== 1 ? 's' : ''} Connected
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Connected Devices */}
              {patientSummary?.connected_devices?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Connected Devices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {patientSummary.connected_devices.map(device => (
                        <Badge key={device.provider} variant="outline" className="py-1 px-3">
                          {device.provider_name || device.provider}
                          <span className="text-xs text-slate-400 ml-2">
                            {device.last_sync ? `Synced ${new Date(device.last_sync).toLocaleDateString()}` : 'Not synced'}
                          </span>
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Latest Readings */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Latest Readings</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(patientSummary?.latest_readings || {}).length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No health data available</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(patientSummary?.latest_readings || {}).map(([type, reading]) => (
                        <div key={type} className={`p-4 rounded-lg border ${reading.status ? statusColors[reading.status] : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            {dataTypeIcons[type] || <Pulse size={20} />}
                            <span className="text-sm font-medium capitalize">{type === 'blood_pressure_systolic' ? 'Blood pressure' : type.replace(/_/g, ' ')}</span>
                          </div>
                          <p className="text-2xl font-bold">
                            {reading.value}{reading.secondary_value ? `/${reading.secondary_value}` : ''}
                            <span className="text-sm font-normal text-slate-500 ml-1">{reading.unit}</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(reading.recorded_at).toLocaleString()}
                          </p>
                          {reading.status && reading.status !== 'normal' && (
                            <div className="flex items-center gap-1 mt-2">
                              <Warning size={14} />
                              <span className="text-xs font-medium capitalize">{reading.status}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Trends */}
              {Object.keys(patientSummary?.trends || {}).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ChartLine size={20} />
                      7-Day Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(patientSummary?.trends || {}).map(([type, trend]) => (
                        <div key={type} className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500 capitalize">{type === 'blood_pressure_systolic' ? 'Blood pressure' : type.replace(/_/g, ' ')}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-lg font-semibold">{trend.current_avg}</span>
                            {getTrendIcon(trend.direction)}
                            <span className={`text-sm ${trend.direction === 'up' ? 'text-red-500' : trend.direction === 'down' ? 'text-green-500' : 'text-slate-400'}`}>
                              {trend.change_percent > 0 ? '+' : ''}{trend.change_percent}%
                            </span>
                          </div>
                          <p className="text-xs text-slate-400">vs previous week</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Glucose Chart Data (if CGM connected) */}
              {glucoseData?.readings?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Drop size={20} className="text-green-600" />
                      CGM Glucose Overview (7 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-sm text-slate-500">Average</p>
                        <p className="text-2xl font-bold">{glucoseData.statistics?.average}</p>
                        <p className="text-xs text-slate-400">mg/dL</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-green-600">Time in Range</p>
                        <p className="text-2xl font-bold text-green-700">{glucoseData.statistics?.in_range_percent}%</p>
                        <p className="text-xs text-green-500">70-180 mg/dL</p>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded-lg text-center">
                        <p className="text-sm text-yellow-600">Below Range</p>
                        <p className="text-2xl font-bold text-yellow-700">{glucoseData.statistics?.below_range_percent}%</p>
                        <p className="text-xs text-yellow-500">&lt;70 mg/dL</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-sm text-red-600">Above Range</p>
                        <p className="text-2xl font-bold text-red-700">{glucoseData.statistics?.above_range_percent}%</p>
                        <p className="text-xs text-red-500">&gt;180 mg/dL</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 text-center">
                      {glucoseData.statistics?.reading_count} readings over {glucoseData.days} days
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
